import type {
	OrderCheckFiringLevel,
	OrderCheckFlag,
	OrderCheckLevel,
} from './order-check-level';
import { isFlagOn, isOrderCheckOn } from './order-check-level';

/**
 * PD-16276 — the account-level credit checks.
 *
 * Both callers take the figures from ClientsService.getAvailableCredit, so
 * the enforced limit is the billing account's when a client is billed with
 * its parent (PD-15850) and the tenant default when the client has none of
 * its own (PD-15855).
 */

export interface CreditHoldFindingModel {
	level: OrderCheckFiringLevel;
}

export function checkCreditHold(
	// Raw database rows carry tinyint(1) as 1/0, entities as booleans.
	hasCreditHold: OrderCheckFlag,
	level: OrderCheckLevel | null | undefined,
): CreditHoldFindingModel | null {
	if (!isOrderCheckOn(level)) {
		return null;
	}

	return isFlagOn(hasCreditHold) ? { level } : null;
}

export interface CreditLimitInputModel {
	/**
	 * The enforced limit. Null, undefined, 0 or less means no limit applies:
	 * an explicit 0 means "not determined", not "no credit" (PD-15708).
	 */
	creditLimit: number | null | undefined;
	/** The ERP-synced receivable balance. */
	creditBalance: number;
	/** Orders submitted here that have not reached the ERP yet. */
	inFlightTotal: number;
	/** This order's total. */
	orderTotal: number;
}

export interface CreditLimitFindingModel {
	level: OrderCheckFiringLevel;
	creditLimit: number;
	creditBalance: number;
	inFlightTotal: number;
	orderTotal: number;
	projectedBalance: number;
	availableCredit: number;
}

/**
 * The order may not take the account past its limit once the ERP balance and
 * every in-flight order are counted. An account already over its limit is
 * flagged even for a zero-total order, which is how the server has always
 * enforced it.
 */
export function checkCreditLimit(
	input: CreditLimitInputModel,
	level: OrderCheckLevel | null | undefined,
): CreditLimitFindingModel | null {
	const creditLimit = input.creditLimit;

	if (!isOrderCheckOn(level) || creditLimit == null || !(creditLimit > 0)) {
		return null;
	}

	const creditBalance = finiteOrZero(input.creditBalance);
	const inFlightTotal = finiteOrZero(input.inFlightTotal);
	const orderTotal = finiteOrZero(input.orderTotal);
	const projectedBalance = creditBalance + inFlightTotal + orderTotal;

	if (!(projectedBalance > creditLimit)) {
		return null;
	}

	return {
		level,
		creditLimit,
		creditBalance,
		inFlightTotal,
		orderTotal,
		projectedBalance,
		availableCredit: creditLimit - creditBalance - inFlightTotal,
	};
}

function finiteOrZero(value: number): number {
	return Number.isFinite(value) ? value : 0;
}
